<template>
  <Modal
    class="self-certification-modal"
    :class="confirmChanges ? 'confirm' : ''"
    :closeBtn="true"
    @close="closeModal"
  >
    <CircleLoader v-if="loading" />
    <template v-else>
      <div class="header" :class="confirmChanges ? 'text-center' : ''">{{ header }}</div>
      <template v-if="confirmChanges">
        <div class="content">
          <p>
            Thank you for providing the self-certification details. The Self-Certification form will
            be included in your final loan documents.
          </p>
          <div class="footer">
            <next-button
              @click="closeModal"
              text="Close"
              class="next-btn"
              :disabled="savingChanges"
              :loading="savingChanges"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="self-certification-details-form">
          <base-form-field v-model="form.costOfAttendance" :schema="schema.costOfAttendance">
            <template #label>
              <div class="label-wrapper" field="costOfAttendance">
                <div>
                  Students cost of attendance for the period of enrollment covered by the loan
                  <span class="required">*</span>
                </div>
                <div class="tuition-costs">
                  <p
                    :class="{ active: tuitionType === 'in-state' }"
                    @click="tuitionType = 'in-state'"
                    data-value="in-state"
                  >
                    In State
                  </p>
                  <p
                    :class="{ active: tuitionType === 'out-of-state' }"
                    data-value="out-of-state"
                    @click="tuitionType = 'out-of-state'"
                  >
                    Out of State
                  </p>
                </div>
              </div>
            </template>
          </base-form-field>
          <base-form-field
            v-model="form.estimatedFinancialAssistance"
            :schema="schema.estimatedFinancialAssistance"
          />
          <div class="form-group" columns="2">
            <base-form-field v-model="form.address1" :schema="schema.address1" />
            <base-form-field v-model="form.address2" :schema="schema.address2" />
          </div>
          <div class="form-group" columns="3">
            <base-form-field v-model="form.city" :schema="schema.city" />
            <base-form-field
              v-model.number="form.stateId"
              :schema="schema.stateId"
              :options="states"
            />
            <base-form-field v-model="form.postalCode" :schema="schema.postalCode" />
          </div>
        </div>
        <div class="footer justify-content-flex-end">
          <div class="form-error">
            {{ formErrorMessage }}
            <p v-if="submissionError">
              Your changes could not be saved. Please try again. If the issue persists, please
              contact our support team at
              <a class="underline" :href="`tel:${hrefTFN}`">{{ tollFreeNumber }}</a> or
              <a class="underline" :href="`mailto:${emailAddress}`">{{ emailAddress }}</a
              >.
            </p>
          </div>
          <div class="footer-btn-group">
            <next-button
              v-if="!confirmChanges"
              :disabled="savingChanges"
              text="Cancel"
              class="cancel-btn"
              @click="cancelChanges"
            ></next-button>
            <next-button
              @click="saveChanges"
              text="Save"
              class="next-btn"
              :disabled="savingChanges"
              :loading="savingChanges"
            />
          </div></div
      ></template>
    </template>
  </Modal>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import Modal from "@/components/shared/Modal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import type { School } from "@/models/school";
import { AddressCountryIds, CustomSchoolTypes } from "@/config/constants";
import type { FieldSchema, FormSchema, Option } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { $api } from "@/services/api";
import useValidation from "@/use/validation";
import type { Stipulation } from "@/models/opal";
import type { StipulationInputRequest, SelfCertificationDetailsInputRequest } from "@/models/api";
import useApplicationData from "@/composables/useApplicationData";

interface TuitionCosts {
  inState: number | string;
  outOfState: number | string;
}

interface Form {
  schoolId: number;
  inState: boolean;
  costOfAttendance?: string | number;
  estimatedFinancialAssistance?: string | number;
  address1?: string;
  address2?: string;
  city?: string;
  stateId?: number;
  countryId?: number;
  postalCode?: string;
}

const { validateForm } = useValidation();

const store = useStore();

interface Props {
  stip: Stipulation;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: "success", updatedStip: Stipulation): void;
}>();

const { applicationId, tollFreeNumber, hrefTFN, emailAddress, productTypeId } =
  useApplicationData();

const submissionError = ref<boolean>(false);

const validForm = ref<boolean>(true);

const formError = computed<boolean>(() => validForm.value === false);

const formErrorMessage = computed<string>(() =>
  formError.value ? "Missing information above. Please review and complete before continuing." : "",
);

const states = computed<Option[]>(() => store.getters["lookupsModule/statesOptions"]);

const schools = computed<School[]>(() => store.state.schools);

const confirmChanges = ref<boolean>(false);

const savingChanges = ref<boolean>(false);

const loading = ref<boolean>(true);

const tuitionType = ref<string>("in-state");

const header = computed<string>(() =>
  confirmChanges.value ? "Self-Certification Details Confirmed" : "Self-Certification Details",
);

const inState = computed<boolean>(() => tuitionType.value === "in-state");

const selfCertificationDetailsInput = computed(() => props.stip?.selfCertificationDetailsInput);

const applicationStipulationId = computed<number>(() => props.stip?.id ?? 0);

const selectedSchool = computed(() =>
  schools.value.find(
    (s: School) => s.id === store.state.appData?.application?.schoolConfiguration?.schoolId,
  ),
);

const selectedEnrollmentTerm = computed<string>(() => {
  return (
    store.state.loanConfigurationModule?.applicationInfo?.selectedEnrollmentTerm?.description ||
    store.state.appData?.application?.schoolConfiguration?.academicTerm
  );
});

const combinedTerms = computed<boolean>(() => (selectedEnrollmentTerm.value || "").includes("-"));

const tuitionCosts = computed<TuitionCosts>(() => {
  let tuitionCosts = {
    inState: 0,
    outOfState: 0,
  } as TuitionCosts;

  //#TODO handle custom program tuitions with their custom terms like Aviation
  if (selectedSchool.value && CustomSchoolTypes.includes(selectedSchool.value?.schoolTypeId)) {
    tuitionCosts.inState = selectedSchool.value?.costOfAttendanceInState ? selectedSchool.value?.costOfAttendanceInState.toFixed(2) : 0;
    tuitionCosts.outOfState = selectedSchool.value?.costOfAttendanceOutOfState ? selectedSchool.value?.costOfAttendanceOutOfState.toFixed(2) : 0;

    return tuitionCosts;
  }

  tuitionCosts.inState = selectedSchool.value?.costOfAttendanceInState
    ? combinedTerms.value === true
      ? selectedSchool.value?.costOfAttendanceInState.toFixed(2)
      : (selectedSchool.value?.costOfAttendanceInState / 2).toFixed(2)
    : 0;

  tuitionCosts.outOfState = selectedSchool.value?.costOfAttendanceOutOfState
    ? combinedTerms.value === true
      ? selectedSchool.value?.costOfAttendanceOutOfState.toFixed(2)
      : (selectedSchool.value?.costOfAttendanceOutOfState / 2).toFixed(2)
    : 0;

  return tuitionCosts;
});

let form = ref<Form>({
  schoolId: computed<number>(() => store.state.appData.application.schoolConfiguration?.schoolId)
    .value,
  inState: true,
  costOfAttendance: undefined,
  estimatedFinancialAssistance: undefined,
  address1: undefined,
  address2: undefined,
  city: undefined,
  stateId: undefined,
  postalCode: undefined,
  countryId: AddressCountryIds.USA,
});

const schema = ref<FormSchema>({
  costOfAttendance: {
    name: "costOfAttendance",
    label: "Students cost of attendance for the period of enrollment covered by the loan",
    type: "money",
    hideLabel: false,
    required: true,
    allowZero: false,
    errorMessage: computed(() =>
      form.value.costOfAttendance && Number(form.value.costOfAttendance) > 0
        ? ""
        : "Cost of attendance is required.",
    ).value,
    allowDecimals: true,
    productTypeId: computed(() => productTypeId.value).value,
    showRequiredIndicator: true,
  } as FieldSchema,
  estimatedFinancialAssistance: {
    name: "estimatedFinancialAssistance",
    label: "Estimated financial assistance for the period of enrollment covered by the loan",
    type: "money",
    required: true,
    allowZero: true,
    allowDecimals: true,
    showRequiredIndicator: true,
    errorMessage: computed(() =>
      form.value.estimatedFinancialAssistance && Number(form.value.estimatedFinancialAssistance) > 0
        ? ""
        : "Please provide a valid amount, or enter 0.",
    ).value,
  },
  address1: {
    name: "address1",
    label: "School Address 1",
    type: "text",
    required: true,
    showRequiredIndicator: true,
  },
  address2: {
    name: "address2",
    type: "text",
    subtype: "address",
    label: "School Address 2",
    required: false,
    showRequiredIndicator: true,
  } as FieldSchema,
  city: {
    name: "city",
    type: "text",
    label: "City",
    subtype: "address",
    required: true,
    showRequiredIndicator: true,
  } as FieldSchema,
  stateId: {
    name: "stateId",
    type: "select",
    subtype: "address",
    label: "State",
    required: true,
    showRequiredIndicator: true,
  } as FieldSchema,
  postalCode: {
    name: "postalCode",
    type: "zipcode",
    label: "Zip Code",
    errorMessage: "Zip Code is required.",
    required: true,
    showRequiredIndicator: true,
  } as FieldSchema,
});

function closeModal() {
  if (confirmChanges.value === true) {
    store.dispatch("checklistModule/updateStipVuexState", {
      stipulation: props.stip,
      title: "Regulatory Verification",
    });

    const body = document.querySelector("body");
    if (body) {
      body.style.overflow = "visible";
    }
  }

  store.dispatch("modal/closeSelfCertificationDetailsModal");

  savingChanges.value = false;
  submissionError.value = false;
  confirmChanges.value = false;
}

function currencyToDecimal(currency: string | number) {
  let decimal;
  if (typeof currency === "number") {
    decimal = currency;
  } else {
    const numberString = currency.replace(/[^0-9.-]+/g, "");
    decimal = parseFloat(numberString);
  }

  return isNaN(decimal) ? 0 : decimal;
}

function resetForm() {
  form.value.inState = true;
  form.value.costOfAttendance = undefined;
  form.value.estimatedFinancialAssistance = undefined;
  form.value.address1 = undefined;
  form.value.address2 = undefined;
  form.value.city = undefined;
  form.value.stateId = undefined;
  form.value.postalCode = undefined;
}

function cancelChanges() {
  resetForm();
  closeModal();
}

async function saveChanges() {
  savingChanges.value = true;
  submissionError.value = false;

  validForm.value = validateForm(schema.value, form.value);

  if (validForm.value) {
    form.value.inState = inState.value;
    form.value.costOfAttendance = form.value.costOfAttendance
      ? currencyToDecimal(form.value.costOfAttendance)
      : form.value.costOfAttendance;

    form.value.estimatedFinancialAssistance = form.value.estimatedFinancialAssistance
      ? currencyToDecimal(form.value.estimatedFinancialAssistance)
      : form.value.estimatedFinancialAssistance;

    const request = {
      applicationStipulationId: applicationStipulationId.value,
      applicationId: applicationId.value,
      selfCertificationDetailsInput: form.value as SelfCertificationDetailsInputRequest,
    } as StipulationInputRequest;

    const response = await $api.applications.addStipInput(applicationId.value, request);

    if (response?.id && response?.id > 0) {
      confirmChanges.value = true;
      savingChanges.value = false;

      if (props.stip) {
        let updatedStip = props.stip;
        updatedStip.stipulationStatusId = response.stipulationStatusId;
        updatedStip.stipulationStatusName = response.stipulationStatusName;
        emit("success", updatedStip);
        await nextTick();
      }
    } else {
      confirmChanges.value = false;
      savingChanges.value = false;
      submissionError.value = true;
    }
  } else {
    savingChanges.value = false;
  }
}

function setCostOfAttendance() {
  form.value.costOfAttendance =
    tuitionType.value === "out-of-state"
      ? tuitionCosts.value.outOfState
      : tuitionCosts.value.inState;

  schema.value.costOfAttendance.errors = [];
  schema.value.costOfAttendance.valid = true;
}

onMounted(async () => {
  if ((store.state.schools || []).length <= 0) {
    const res = await $api.school.getSchoolsByProgram(
      store?.state?.appData?.application?.programTypeId,
      store?.state?.appData?.application?.productTypeId,
      store?.state?.appData?.application?.lenderId,
    );

    store.dispatch("setSchools", res.schools);
  }

  setCostOfAttendance();

  nextTick(() => {
    loading.value = false;
  });
});

watch(
  () => tuitionType.value,
  () => {
    setCostOfAttendance();
  },
);

defineExpose({
  selfCertificationDetailsInput,
});
</script>

<style scoped src="@/styles/modal/modal.css"></style>
<style scoped>
* {
  font-family: var(--header-font);
}

.self-certification-modal:is(.confirm) .header {
  text-align: center;
}

.self-certification-modal:is(.confirm) .footer {
  justify-content: center;
  margin-bottom: 25px;
}

.self-certification-modal:is(.confirm) .content {
  padding: 0 40px;
  text-align: center;
}

.self-certification-modal:is(.confirm) .content p {
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 17px; /* 121.429% */
}

.self-certification-modal .self-certification-details-form {
  padding: 0 30px;
}

.footer .form-error {
  color: #e21837;
  font-family: var(--header-font);
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: 17px; /* 121.429% */
}

.footer .form-error p {
  margin-bottom: 0;
}

.footer .form-error a {
  color: #e21837;
  text-decoration: underline;
}

.self-certification-modal .footer .footer-btn-group {
  display: flex;
  flex-direction: row;
  gap: 15px;
  justify-content: flex-end;
}

.self-certification-modal .footer button {
  display: flex;
  width: 105px;
  height: 34px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  color: #000;
  font-family: var(--header-font);
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 20px; /* 142.857% */
}

.self-certification-modal .footer button.cancel-btn {
  border-radius: 4px;
  background: #f2f3f5;
  color: #000;
  border: none;
}

.self-certification-modal .footer button.next-btn {
  border-radius: 4px;
  background: var(--theme-color);
  color: #fff;
  font-family: var(--header-font);
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: 20px; /* 142.857% */
}

.self-certification-modal .self-certification-details-form .form-field {
  padding-bottom: 20px;
}
.self-certification-modal .header {
  padding: 0 30px 20px 30px;
  color: #000;
  font-family: var(--header-font);
  font-size: 25px;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: normal;
}

.self-certification-modal .tuition-costs {
  display: flex;
  align-items: center;
  border-radius: 40px;
  background: #f2f3f5;
  min-width: 190px;
  cursor: pointer;
}
.self-certification-modal .tuition-costs p {
  margin-bottom: 0;
  color: #000000;
  font-family: var(--header-font);
  font-size: 12px;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 16px; /* 133.333% */
}
.self-certification-modal .tuition-costs p:not(.active) {
  padding: 7px 11px 7px 11px;
}

.self-certification-modal .tuition-costs p.active {
  background: var(--theme-color);
  color: #ffffff;
  font-weight: var(--font-weight-semibold);
}

.self-certification-modal .tuition-costs p[data-value="in-state"]:not(.active) {
  padding: 7px 23px 7px 24px;
}
.self-certification-modal .tuition-costs p[data-value="in-state"].active {
  border-radius: 40px 0px 0px 40px;
  padding: 7px 23px 7px 24px;
}
.self-certification-modal .tuition-costs p[data-value="out-of-state"].active {
  border-radius: 0px 40px 40px 0px;
  padding: 7px 11px 7px 11px;
}
.self-certification-modal .tuition-costs p[data-value="out-of-state"]:not(.active) {
  padding-left: 11px;
}
.self-certification-modal .self-certification-details-form .label-wrapper {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.form-group {
  margin-bottom: 0;
}

.form-group[columns="2"] {
  display: flex;
  flex-direction: row;
  gap: 46px;
}

.form-group[columns="2"] .form-field {
  width: 50%;
}

.form-group[columns="3"] {
  display: flex;
  flex-direction: row;
  gap: 46px;
}
:nth-child(1 of .form-group[columns="3"] .form-field) {
  width: 25%;
}

.self-certification-modal .form-field label .required {
  color: #e21837;
}

.self-certification-modal .form-group[columns="3"] .form-field {
  min-width: 100% / 3;
}

@media (max-width: 763px) {
  .form-group[columns="2"],
  .form-group[columns="3"] {
    flex-direction: column;
    gap: 0;
  }

  .form-group[columns="2"] .form-field,
  .form-group[columns="3"] .form-field {
    width: 100%;
  }

  :nth-child(1 of .form-group[columns="3"] .form-field) {
    width: 100%;
  }
  .self-certification-modal .footer {
    flex-direction: column;
  }
  .self-certification-modal .footer .footer-btn-group {
    width: 100%;
  }

  .self-certification-modal .footer .footer-btn-group button {
    width: 50%;
  }
  .self-certification-modal .form-error {
    margin-bottom: 11px;
    text-align: center;
  }
}

@media (max-width: 418px) {
  .self-certification-modal .header {
    line-height: 26px;
    padding-bottom: 20px;
  }
  .self-certification-modal
    .self-certification-details-form
    .label-wrapper:is([field="costOfAttendance"]) {
    flex-direction: column;
    align-items: flex-start;
  }

  .self-certification-modal .tuition-costs {
    min-width: 100%;
  }
  .self-certification-modal .tuition-costs p {
    width: 50%;
    text-align: center;
  }
}
</style>
